/**
 * Client portal « Paramètres » (W5, B3-ClientSettings, ENG-19, DUP-1, DUP-2).
 *
 * What a viewer (a client of the agency) may change, and nothing else:
 *   - Profil: the full name of its own account (email and company read only);
 *   - Notifications: the preferences of its customer's link, persisted on the
 *     server (`PATCH /api/portal/settings`). A switch saves at once and the
 *     toast offers « Annuler » (reversible action, DES-13).
 * The Calendly and CRM (Swoft) configuration is the agency's: it is edited in
 * the dialer (Réglages > Intégrations), the portal only says who to ask
 * (DUP-1, DUP-2). The former « Réinitialiser données d'appels » called a super
 * admin route (403 for a client) and is gone.
 * Every failure is said on screen: translated message, then the code (DES-16).
 *
 * Props are kept for ClientDashboard: `onUpdate` runs after the profile is
 * saved; `client` is only a fallback for the read-only fields while loading.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ApiErrorException } from '@/api/client';
import {
  getPortalSettings,
  updatePortalSettings,
  PORTAL_SETTINGS_QUERY_KEY,
  type NotificationPrefKey,
  type NotificationPrefs,
  type PortalSettings,
} from '@/api/endpoints/portal';

interface ClientLite {
  email?: string | null;
  company_name?: string | null;
  contact_name?: string | null;
}

interface SettingsSectionProps {
  client?: ClientLite | null;
  onUpdate?: () => void;
}

const EVENTS = ['weekly_report', 'monthly_goal', 'new_meeting', 'new_prospect'] as const;
const EVENT_LABEL: Record<(typeof EVENTS)[number], string> = {
  weekly_report: 'weeklyReport',
  monthly_goal: 'monthlyGoal',
  new_meeting: 'newMeeting',
  new_prospect: 'newProspect',
};
const CHANNELS = ['app', 'email'] as const;

const codeOf = (err: unknown): string => (err instanceof ApiErrorException ? err.code : 'NETWORK');

function ErrorLine({ message, code }: { message: string; code?: string }) {
  return (
    <div role="alert" className="text-[13px]" style={{ color: 'var(--vf-status-failed-fg)' }}>
      <p>{message}</p>
      {code && <p className="font-vf-mono text-[11px] text-[var(--vf-text-3)]">{code}</p>}
    </div>
  );
}

export default function SettingsSection({ client, onUpdate }: SettingsSectionProps) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: PORTAL_SETTINGS_QUERY_KEY,
    queryFn: ({ signal }) => getPortalSettings({ signal }),
    staleTime: 30_000,
  });
  const settings = query.data;

  const [fullName, setFullName] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [prefsError, setPrefsError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<NotificationPrefKey | null>(null);

  useEffect(() => {
    if (settings) setFullName(settings.profile.full_name);
  }, [settings?.profile.full_name]); // eslint-disable-line react-hooks/exhaustive-deps

  // The profile's answer only replaces the profile: a switch saved meanwhile keeps its value.
  const storeProfile = (next: PortalSettings) => queryClient.setQueryData<PortalSettings>(
    PORTAL_SETTINGS_QUERY_KEY, (cur) => (cur ? { ...cur, profile: next.profile } : next),
  );

  const saveProfile = async () => {
    setProfileError(null);
    if (!fullName.trim()) {
      setProfileError('PORTAL_PROFILE_INVALID');
      return;
    }
    setProfileSaving(true);
    try {
      storeProfile(await updatePortalSettings({ full_name: fullName }));
      toast.success(t('admin:backoffice.settings.save.success'));
      onUpdate?.();
    } catch (err) {
      setProfileError(codeOf(err));
    } finally {
      setProfileSaving(false);
    }
  };

  /** Changes one preference in the cache, whatever else changed meanwhile. */
  const patchPref = (key: NotificationPrefKey, value: boolean) => {
    queryClient.setQueryData<PortalSettings>(PORTAL_SETTINGS_QUERY_KEY, (cur) => (
      cur?.notification_prefs ? { ...cur, notification_prefs: { ...cur.notification_prefs, [key]: value } } : cur
    ));
  };

  /**
   * One switch: shown at once, saved, then « Annuler » in the toast.
   * REV-W5-4: only this key is touched, read from the current cache (never
   * from the settings of the render that showed the toast, which put back
   * the other switches and the name). A failure puts back the server's
   * truth (refetch), so a failed « Annuler » does not pretend it worked.
   */
  const setPref = async (key: NotificationPrefKey, value: boolean, undoable = true) => {
    const before = queryClient.getQueryData<PortalSettings>(PORTAL_SETTINGS_QUERY_KEY)?.notification_prefs?.[key];
    if (typeof before !== 'boolean') return;
    setPrefsError(null);
    setSavingKey(key);
    patchPref(key, value);
    try {
      const saved = await updatePortalSettings({ notification_prefs: { [key]: value } as Partial<NotificationPrefs> });
      const stored = saved.notification_prefs?.[key];
      patchPref(key, typeof stored === 'boolean' ? stored : value);
      if (undoable) {
        toast.success(t('admin:backoffice.settings.notifications.saved'), {
          action: { label: t('common:actions.undo'), onClick: () => { void setPref(key, !value, false); } },
        });
      }
    } catch (err) {
      patchPref(key, before);
      setPrefsError(codeOf(err));
      void queryClient.invalidateQueries({ queryKey: PORTAL_SETTINGS_QUERY_KEY });
    } finally {
      setSavingKey(null);
    }
  };

  if (query.isLoading) {
    return (
      <div className="max-w-2xl space-y-4" role="status" aria-label={t('common:actions.loading')}>
        {[0, 1, 2].map((i) => <div key={i} className="h-32 animate-pulse rounded-xl bg-[var(--vf-surface-3)] motion-reduce:animate-none" />)}
      </div>
    );
  }

  if (query.isError || !settings) {
    return (
      <div className="max-w-2xl space-y-3 rounded-xl border p-6" style={{ borderColor: 'var(--vf-border)' }}>
        <ErrorLine message={t('admin:backoffice.settings.loadError')} code={codeOf(query.error)} />
        <Button variant="outline" onClick={() => { void query.refetch(); }}>{t('common:actions.retry')}</Button>
      </div>
    );
  }

  const prefs = settings.notification_prefs;
  const agency = settings.agency_name || t('admin:backoffice.settings.integrations.yourAgency');
  const card = 'rounded-xl border p-4 sm:p-6 space-y-4';
  const cardStyle = { borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' };

  return (
    <div className="max-w-2xl space-y-6">
      <section className={card} style={cardStyle} aria-labelledby="portal-settings-profile">
        <h2 id="portal-settings-profile" className="font-semibold text-[var(--vf-text-1)]">{t('admin:backoffice.settings.profile')}</h2>
        <div className="space-y-2">
          <Label htmlFor="portal-full-name">{t('admin:backoffice.settings.personalInfo.fullName')}</Label>
          <Input
            id="portal-full-name"
            value={fullName}
            maxLength={120}
            onChange={(e) => setFullName(e.target.value)}
            placeholder={t('admin:backoffice.settings.personalInfo.fullNamePlaceholder')}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="portal-email">{t('common:field.email')}</Label>
          <Input id="portal-email" value={settings.profile.email || client?.email || ''} disabled />
          <p className="text-xs text-[var(--vf-text-3)]">{t('admin:backoffice.settings.personalInfo.emailImmutable')}</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="portal-company">{t('admin:backoffice.settings.personalInfo.companyName')}</Label>
          <Input id="portal-company" value={settings.profile.company_name || client?.company_name || ''} disabled />
        </div>
        {profileError && (
          <ErrorLine
            // i18n-exempt: an error code compared, not text
            message={profileError === 'PORTAL_PROFILE_INVALID' ? t('admin:backoffice.settings.personalInfo.nameRequired') : t('admin:backoffice.settings.save.error')}
            code={profileError}
          />
        )}
        <div className="flex justify-end">
          <Button onClick={() => { void saveProfile(); }} disabled={profileSaving || fullName.trim() === settings.profile.full_name}>
            {profileSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />}
            {profileSaving ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        </div>
      </section>

      <section className={card} style={cardStyle} aria-labelledby="portal-settings-notifications">
        <h2 id="portal-settings-notifications" className="font-semibold text-[var(--vf-text-1)]">{t('admin:notifications.title')}</h2>
        {prefs ? (
          <div className="relative overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">{t('admin:notifications.title')}</caption>
              <thead>
                <tr className="text-left text-xs text-[var(--vf-text-3)]">
                  <th scope="col" className="py-2 pr-4 font-medium">{t('admin:backoffice.settings.notifications.event')}</th>
                  <th scope="col" className="py-2 px-2 font-medium text-center">{t('admin:backoffice.settings.notifications.inApp')}</th>
                  <th scope="col" className="py-2 pl-2 font-medium text-center">{t('common:field.email')}</th>
                </tr>
              </thead>
              <tbody>
                {EVENTS.map((event) => (
                  <tr key={event} className="border-t" style={{ borderColor: 'var(--vf-border)' }}>
                    <th scope="row" className="py-2 pr-4 text-left font-normal text-[var(--vf-text-1)]">
                      {t(`admin:backoffice.settings.notifications.${EVENT_LABEL[event]}`)}
                    </th>
                    {CHANNELS.map((channel) => {
                      const key = `${event}_${channel}` as NotificationPrefKey;
                      return (
                        <td key={channel} className="py-1 px-2 text-center">
                          <span className="inline-flex min-h-11 min-w-11 items-center justify-center">
                            <Switch
                              checked={prefs[key]}
                              disabled={savingKey === key}
                              aria-label={t('admin:backoffice.settings.notifications.switchLabel', {
                                event: t(`admin:backoffice.settings.notifications.${EVENT_LABEL[event]}`),
                                channel: channel === 'app' ? t('admin:backoffice.settings.notifications.inApp') : t('common:field.email'),
                              })}
                              onCheckedChange={(v) => { void setPref(key, v); }}
                            />
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-[var(--vf-text-2)]">{t('admin:backoffice.settings.notifications.noCustomer', { agency })}</p>
        )}
        {prefsError && <ErrorLine message={t('admin:backoffice.settings.save.error')} code={prefsError} />}
      </section>

      <section className={card} style={cardStyle} aria-labelledby="portal-settings-integrations">
        <h2 id="portal-settings-integrations" className="font-semibold text-[var(--vf-text-1)]">{t('admin:backoffice.settings.tabs.integrations')}</h2>
        <p className="text-sm text-[var(--vf-text-2)]">{t('admin:backoffice.settings.integrations.managedByAgency', { agency })}</p>
      </section>
    </div>
  );
}
